import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Camera, Globe, Clock, CheckCircle2, AlertCircle, Handshake, List } from "lucide-react";
import { useBiens } from "@/hooks/useBiens";
import { DataTable } from "@/v2/components/DataTable";
import { usePublicationColumns } from "@/v2/components/publication/usePublicationColumns";
import { cn } from "@/lib/utils";
import { isCLevelReady } from "@/v2/lib/bienLifecycle";
import { isForRent } from "@/lib/transaction";
import { hasUnpublishedChanges } from "@/lib/publicationHelpers";
import { isPublishable, type PublishGateInput } from "@/lib/publishGate";
import { BienDetailModal } from "@/v2/components/BienDetailModal";
import { useLatestPortalPublicationsByBien } from "@/hooks/usePortalPublications";
import { useRole } from "@/contexts/RoleContext";
import type { BienRow } from "@/schemas/bien";

type Tab = "all" | "todo" | "ready" | "published" | "stale" | "sousOption";

const isActive = (b: BienRow) =>
  ["EN PRÉPARATION", "DISPONIBLE"].includes((b.disponibilite || "").toUpperCase());

// Delegue au helper canonique (src/lib/publicationHelpers.ts) pour eviter la
// derive entre buckets V2, banniere fiche bien, et tuile dashboard.
const isStale = (b: BienRow) =>
  hasUnpublishedChanges({
    statutPublication: b.statutPublication,
    updated_at: b.updated_at,
    last_published_at: b.last_published_at,
    content_updated_at: b.content_updated_at,
  } as Parameters<typeof hasUnpublishedChanges>[0]);


export function PublicationWorkspacePage() {
  const { t } = useTranslation();
  const { role } = useRole();
  // Plus de roleOverride "publication_suivi" depuis la migration 113 : le rôle
  // `publication` a absorbé les droits de ses deux sous-rôles et voit
  // désormais toutes les colonnes dont les buckets ont besoin
  // (statutPublication, last_published_at, content_updated_at, dossierCloture).
  //
  // Ça compte : `isCLevelReady` et `isPublishable` ci-dessous calculent une
  // règle sur la LIGNE ENTIÈRE. Une seule colonne strippée par la RPC et le
  // bien est jugé incomplet, donc absent de tous les buckets — c'est le bug
  // qui avait vidé /apparts-for-rent en collaborateur le 2026-08-09.
  const { data: biens, isLoading } = useBiens();
  const [tab, setTab] = useState<Tab>("todo");
  // Modale fiche bien — même pattern que Apparts for Rent / Pipeline : on
  // ouvre le détail en surimpression pour préserver la vue Publication en
  // cours (tabs, recherche, scroll).
  const [modalBienId, setModalBienId] = useState<string | null>(null);
  // Statut Immoweb réel par bien (colonne « Immoweb » des onglets Prêts /
  // Publiés / Modifs) — demande Thibaud 2026-09-16.
  const { data: immowebByBien } = useLatestPortalPublicationsByBien("immoweb");


  const buckets = useMemo(() => {
    // Gate global du workspace Publication :
    //  1. LOCATION uniquement (isForRent) — cette page est le pendant location
    //     de PublicationVenteWorkspacePage (qui filtre côté vente avec
    //     isForSale, :62) ; le filtre transaction manquait ici, laissant
    //     passer des biens vente "disponibles" avec les champs C-Level
    //     remplis (signalé Thibaud 2026-09-15 : "2 biens à vendre" dans la
    //     liste location).
    //  2. débloqué CLevel (champs requis remplis) ;
    //  3. DISPONIBLE / EN PRÉPARATION uniquement (isActive) — un bien LOUÉ ou
    //     VENDU n'a rien à faire en Publication (décision user 2026-06-02).
    const list = (biens || []).filter(
      (b) =>
        isForRent(b.transaction) &&
        isCLevelReady(b as unknown as Parameters<typeof isCLevelReady>[0]).ok &&
        isActive(b),
    );
    // Onglet « Sous option » EXCLUSIF (demande Thibaud 2026-09-16, mêmes 5
    // onglets que Publication Vente) : un bien en négociation est retiré de la
    // vitrine et ne peut pas être publié tant que la case reste cochée — il
    // n'a rien à faire dans « À préparer ».
    const sousOption = list.filter((b) => b.sousOption === true);
    const open = list.filter((b) => b.sousOption !== true);
    // Buckets alignés sur le garde-fou de publication (publishGate, liste
    // client 2026-06-11) : "Prêts à publier" = exactement les biens dont le
    // bouton Publier est actif. "À préparer" = le reste (non publié).
    const todo = open.filter(
      (b) => b.statutPublication !== "Publié" && !isPublishable(b as unknown as PublishGateInput).ok,
    );
    const ready = open.filter(
      (b) => b.statutPublication !== "Publié" && isPublishable(b as unknown as PublishGateInput).ok,
    );
    const published = open.filter((b) => b.statutPublication === "Publié" && !isStale(b));
    const stale = open.filter(isStale);
    // « Tous » = tout le périmètre de la page, sans découpage (demande Thibaud 2026-09-16).
    return { all: list, todo, ready, published, stale, sousOption };
  }, [biens]);

  const tabs: { key: Tab; label: string; icon: typeof Camera; count: number; color: string }[] = [
    {
      key: "all",
      label: t("v2.publicationWs.tabs.all"),
      icon: List,
      count: buckets.all.length,
      color: "text-text-muted",
    },
    {
      key: "todo",
      label: t("v2.publicationWs.tabs.todo"),
      icon: AlertCircle,
      count: buckets.todo.length,
      color: "text-warning",
    },
    {
      key: "ready",
      label: t("v2.publicationWs.tabs.ready"),
      icon: CheckCircle2,
      count: buckets.ready.length,
      color: "text-accent",
    },
    {
      key: "published",
      label: t("v2.publicationWs.tabs.published"),
      icon: Globe,
      count: buckets.published.length,
      color: "text-success",
    },
    {
      key: "stale",
      label: t("v2.publicationWs.tabs.stale"),
      icon: Clock,
      count: buckets.stale.length,
      color: "text-danger",
    },
    {
      key: "sousOption",
      label: t("v2.publicationWs.tabs.sousOption"),
      icon: Handshake,
      count: buckets.sousOption.length,
      color: "text-warning",
    },
  ];

  const visibleRows = buckets[tab] || [];

  // Colonnes COMMUNES Location / Vente (décision Thibaud 2026-09-16).
  const { columns, minWidth } = usePublicationColumns({ transaction: "location", immowebByBien });

  return (
    <div className="p-4 md:p-6">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2">
          <Camera size={22} className="text-accent" />
          {/* Titre propre à la page (pendant location de « Publication vente »),
              distinct du libellé de navigation — demande Thibaud 2026-09-16. */}
          {t("v2.publicationWs.title")}
        </h1>
        <p className="text-sm text-text-muted mt-1">
          {t("v2.publicationWs.subtitle")}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-border overflow-x-auto">
        {tabs.map((tabItem) => {
          const Icon = tabItem.icon;
          const isActiveTab = tab === tabItem.key;
          return (
            <button
              key={tabItem.key}
              type="button"
              onClick={() => setTab(tabItem.key)}
              className={cn(
                "px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap",
                isActiveTab
                  ? "border-accent text-text-primary"
                  : "border-transparent text-text-muted hover:text-text-primary",
              )}
            >
              <Icon size={14} className={isActiveTab ? "text-accent" : tabItem.color} />
              <span>{tabItem.label}</span>
              <span
                className={cn(
                  "text-xs px-1.5 rounded",
                  isActiveTab ? "bg-accent-light text-accent" : "bg-gray-100 text-text-muted",
                )}
              >
                {tabItem.count}
              </span>
            </button>
          );
        })}
      </div>

      <DataTable
        rows={visibleRows}
        columns={columns}
        rowKey={(b) => b.id}
        onRowClick={(b) => setModalBienId(b.id)}
        searchAccessor={(b) =>
          [b.referenceInterne, b.reference, b.id, b.adresse, b.ville, b.projet]
            .filter(Boolean)
            .join(" ")
        }
        searchPlaceholder={t("v2.publicationWs.searchPlaceholder")}
        minWidth={minWidth}
        isLoading={isLoading}
        emptyLabel={t("v2.publicationWs.empty")}
      />

      {/* Plus de roleOverride : la fiche s'ouvre avec le rôle réel. C'était le
          dernier endroit où le même bien donnait deux jeux de droits selon la
          porte empruntée — complet ici, amputé par /v2/biens/:id. */}
      {/* Un utilisateur au rôle `publication` vient ici pour la
          Commercialisation, pas pour "Le bien" (défaut de la fiche) —
          demande Thibaud 2026-08-20. Les autres rôles (c_level, admin...)
          qui passent par ce même workspace gardent le défaut normal. */}
      <BienDetailModal
        bienId={modalBienId}
        onClose={() => setModalBienId(null)}
        initialTab={role === "publication" ? "commercialisation" : undefined}
      />
    </div>
  );
}
